import { Dropdown, IconButton, IDropdownOption, IDropdownStyles, Label, Link, Separator, Stack, Text } from '@fluentui/react';
import { RootState } from 'app/store';
import { AppLinkToExternal, textProp } from 'global/Application';
import { IVisibilityScope } from 'global/Visibility/IVisibilityScope.type';
import {
    bcbs_config_optionsetitem as IXrmTypes_bcbs_config_optionsetitem
} from 'model/CRM';
import {
    IFlexibleField as FAM_IFlexibleField,
    IGroupConfigurationConfig as FAM_IGroupConfigurationConfig,
    IMultiselectOptionSetValue as FAM_IMultiselectOptionSetValue,
    IMultiselectOptionSetValueValue as FAM_IMultiselectOptionSetValueValue
} from 'model/FlexibleAttributeModel';
import React, { useMemo, useState } from 'react';
import { getOptionsetItems } from './OptionsetControl';
import InformationCallout from './InformationCallout';
import { EHideReason } from 'global/Visibility/EHideReason';
import { ControlDebugRenderVisibility } from './ControlDebug';

interface IProps {
    [key: string]: any;
    fieldValueOnChange?: (event: any, value: FAM_IMultiselectOptionSetValue | null) => void;
    handleChange?: any;
    defaultFieldValue?: FAM_IMultiselectOptionSetValue;
    field: FAM_IFlexibleField;
    config: FAM_IGroupConfigurationConfig;
    state?: RootState;
    visibility?: IVisibilityScope;
    disabled?: boolean;
    label?: string;
    placeholder?: string;
    multiSelect?: boolean;
    defaultSelectedKey?: any;

}


// TODO rename this to OptionSetControl
const MultiselectOptionsetControl: React.FunctionComponent<IProps> = (props) => {

    const [reset, setReset] = useState(0);

    const [selectedKeys, setSelectedKeys] = useState(props.defaultFieldValue?.options ?? []);

    const { options, optionLookup } = useMemo(() => {
        const { options, optionLookup } = getOptionsetItems(props.config, props.field.configRef, props, selectedKeys);
        return { options, optionLookup };
    }, [
        reset,
        props?.state?.status?.debug?.admin,
        props?.state?.status?.debug?.diagnosis
    ]);

    const style = useMemo(() => {

        const dropdownStyles: Partial<IDropdownStyles> = {
            dropdownOptionText: {
                overflow: 'visible',
                whiteSpace: 'normal'
            },
            dropdownItem: {
                height: 'auto',
            },
        };

        return { dropdownStyles };
    }, [])

    const fieldValueOnChange = (
        event: React.FormEvent<HTMLDivElement> | null,
        option?: IDropdownOption<IXrmTypes_bcbs_config_optionsetitem> | null,
        index?: number | undefined,
        reset?: boolean | undefined
    ) => {
        let selected: FAM_IMultiselectOptionSetValueValue[] = [...selectedKeys];

        if (option) {
            if (option.selected) {
                selected.push({
                    id: option.data?.bcbs_config_optionsetitemid!,
                    name: option.data?.bcbs_name!,
                    value: option.data?.bcbs_value!
                });
            } else {
                selected = selected.filter(key => key.id !== option.id);
            }
        }

        if (reset) {
            selected = [];
        }

        // Filter options: keep only valid non-expired options, or standalone option only
        const filtered: FAM_IMultiselectOptionSetValueValue[] = [];
        let standaloneOption: FAM_IMultiselectOptionSetValueValue | null = null;

        for (const item of selected) {
            const opt = optionLookup[item.id];
            const optionSetItem = opt?.data;

            // Check if standalone option
            if (optionSetItem?.bcbs_isstandalone) {
                standaloneOption = {
                    id: optionSetItem.bcbs_config_optionsetitemid!,
                    name: optionSetItem.bcbs_name!,
                    value: optionSetItem.bcbs_value!
                };
                break;
            }

            // Add valid non-hidden options
            if (optionSetItem && !opt.hidden) {
                filtered.push(item);
            }
        }

        const finalSelection = standaloneOption ? [standaloneOption] : filtered;
        finalSelection.sort((a, b) => a.name.localeCompare(b.name));

        setReset(prev => prev + 1);
        setSelectedKeys(finalSelection);

        const value: FAM_IMultiselectOptionSetValue | null = finalSelection.length === 0
            ? null
            : {
                type: 'multioptionset',
                entityType: 'bcbs_config_optionsetitem',
                value: finalSelection.map(key => key.name).join(", "),
                options: finalSelection
            };

        props.fieldValueOnChange?.(event, value);
    };

    if (props.disabled)
        return <div style={textProp.root.style}>
            <Label styles={textProp.label}>{props.label}</Label>
            <Text styles={textProp.textStyles}>{selectedKeys?.map(t => t.name).join(", ") ?? '---'}</Text>
        </div>;


    return (
        <Dropdown
            placeholder={props.placeholder}
            label={props.label}
            options={options ?? []}
            styles={style.dropdownStyles}
            disabled={props.disabled}
            title={selectedKeys.map(key => key.name).join(',\n')}
            onRenderItem={(scopeProps, defaultRender) => {
                const defaultRenderElement = defaultRender?.(scopeProps);

                const disabledReason = (scopeProps as any)?.disabledReason as EHideReason;

                if (props.state?.status?.debug?.diagnosis) {
                    return <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <InformationCallout>
                            <Stack>
                                <Link
                                    href={AppLinkToExternal({
                                        type: 'bcbs_config_optionsetitem',
                                        id: scopeProps?.data.bcbs_config_optionsetitemid!
                                    })}
                                    target={"_blank"}
                                    rel={"noopener noreferrer"}
                                >
                                    ⚙️ OptionSet Item
                                </Link>
                                <Separator />
                                {ControlDebugRenderVisibility(disabledReason)}
                            </Stack>
                        </InformationCallout>
                        {defaultRenderElement}
                        {/* {scopeProps?.title && <span style={{ fontSize: '10px', color: '#666' }}>{scopeProps.title}</span>} */}
                    </div>;
                }

                return <div style={{ width: '100%', height: '100%' }}>
                    {defaultRenderElement}
                </div>;
            }}
            onRenderCaretDown={(scopeProps, defaultRender) => {
                const defaultRenderElement = defaultRender?.(scopeProps);

                return <>
                    {/* <Stack horizontal verticalAlign={"center"}></Stack> */}
                    <IconButton
                        iconProps={{ iconName: 'Clear', styles: { root: { fontSize: 'x-small', padding: '5px' } } }}
                        styles={{ root: { height: '100%', } }}
                        onClick={(event) => {
                            event.stopPropagation();
                            fieldValueOnChange(null, null, undefined, true);
                        }}
                    />
                    {/* <Icon
                        iconName={"ChevronDown"}
                        styles={{
                            root: {
                                color: "rgb(96, 94, 92)",
                                "&:hover": {
                                    fontWeight: 800
                                }
                            }
                        }}
                        onClick={(event) => event?.currentTarget?.parentNode?.onClick!}
                    /> */}
                    {defaultRenderElement}
                </>;

            }}
            selectedKeys={selectedKeys.map(key => key.id)}
            onChange={fieldValueOnChange}
            multiSelect={true}
            {...props.other}
        />
    );
}

export default MultiselectOptionsetControl;